import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api, getStoredToken } from "@/lib/api";
import { connectNotificationsWS } from "@/lib/notifications/ws-client";
import { useAuth } from "@/contexts/AuthContext";

const NotificationsContext = createContext({
    notifications: [],
    unread: 0,
    drawerOpen: false,
    wsStatus: "idle",
    openDrawer: () => {},
    closeDrawer: () => {},
    markRead: async () => {},
    markAllRead: async () => {},
    remove: async () => {},
    testEmit: async () => {},
});

export function NotificationsProvider({ children }) {
    const { user } = useAuth();
    const [notifications, setNotifications] = useState([]);
    const [unread, setUnread] = useState(0);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [wsStatus, setWsStatus] = useState("idle");
    const wsRef = useRef(null);

    const recomputeUnread = (list) => list.filter((n) => !n.read).length;

    const refresh = useCallback(async () => {
        if (!user) {
            setNotifications([]); setUnread(0); return;
        }
        try {
            const { data } = await api.get("/notifications", { params: { limit: 50 } });
            setNotifications(data.notifications || []);
            setUnread(recomputeUnread(data.notifications || []));
        } catch (_e) { /* offline */ }
    }, [user]);

    useEffect(() => { refresh(); }, [refresh]);

    // WS connect/disconnect on auth change
    useEffect(() => {
        if (wsRef.current) {
            wsRef.current.close();
            wsRef.current = null;
        }
        if (!user) return;
        const token = getStoredToken();
        if (!token) return;
        const handle = connectNotificationsWS({
            token,
            onMessage: (msg) => {
                if (msg.kind === "notification" && msg.data) {
                    setNotifications((prev) => {
                        const next = [msg.data, ...prev.filter((n) => n.id !== msg.data.id)];
                        setUnread(recomputeUnread(next));
                        return next;
                    });
                } else if (msg.kind === "snapshot" && Array.isArray(msg.data)) {
                    // Merge any unseen
                    setNotifications((prev) => {
                        const map = new Map(prev.map((n) => [n.id, n]));
                        msg.data.forEach((n) => map.set(n.id, n));
                        const merged = Array.from(map.values()).sort(
                            (a, b) => (b.created_at || "").localeCompare(a.created_at || ""),
                        );
                        setUnread(recomputeUnread(merged));
                        return merged;
                    });
                }
            },
            onStatus: (s) => setWsStatus(s.kind),
        });
        wsRef.current = handle;
        return () => { handle.close(); };
    }, [user]);

    // Periodic refresh fallback (every 30s) in case WS is down
    useEffect(() => {
        if (!user) return;
        const id = setInterval(refresh, 30_000);
        return () => clearInterval(id);
    }, [user, refresh]);

    const markRead = useCallback(async (id) => {
        try {
            await api.patch(`/notifications/${id}/read`);
            setNotifications((prev) => {
                const next = prev.map((n) => (n.id === id ? { ...n, read: true } : n));
                setUnread(recomputeUnread(next));
                return next;
            });
        } catch (_e) { /* noop */ }
    }, []);

    const markAllRead = useCallback(async () => {
        try {
            await api.post("/notifications/mark-all-read");
            setNotifications((prev) => {
                const next = prev.map((n) => ({ ...n, read: true }));
                setUnread(0);
                return next;
            });
        } catch (_e) { /* noop */ }
    }, []);

    const remove = useCallback(async (id) => {
        try {
            await api.delete(`/notifications/${id}`);
            setNotifications((prev) => {
                const next = prev.filter((n) => n.id !== id);
                setUnread(recomputeUnread(next));
                return next;
            });
        } catch (_e) { /* noop */ }
    }, []);

    const testEmit = useCallback(async () => {
        await api.post("/notifications/test-emit", {
            title: "Test signal",
            body: "Manual test from /notifications/test-emit",
            type: "system_message",
        });
    }, []);

    return (
        <NotificationsContext.Provider
            value={{
                notifications, unread, drawerOpen, wsStatus,
                openDrawer: () => setDrawerOpen(true),
                closeDrawer: () => setDrawerOpen(false),
                markRead, markAllRead, remove, testEmit,
            }}
        >
            {children}
        </NotificationsContext.Provider>
    );
}

export const useNotifications = () => useContext(NotificationsContext);
